import { useCallback, useEffect, useRef, useState } from 'react';

interface UseFirestoreSyncOptions {
  enabled: boolean;
  throttleMs?: number;
  performSync: () => Promise<void>;
}

interface FirestoreSyncHandle {
  requestSync: (force?: boolean) => Promise<void>;
  syncNow: () => Promise<void>;
  markDirty: () => void;
  syncing: boolean;
  error: string | null;
  lastSync: number;
}

export function useFirestoreSync({
  enabled,
  throttleMs = 30_000,
  performSync,
}: UseFirestoreSyncOptions): FirestoreSyncHandle {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState(0);
  const dirtyRef = useRef(true);
  const syncingRef = useRef(false);
  const lastSyncRef = useRef(0);

  const runSync = useCallback(
    async (force = false) => {
      if (!enabled) {
        return;
      }

      if (syncingRef.current) {
        return;
      }

      const throttled = Date.now() - lastSyncRef.current < throttleMs;
      if (!force && throttled) {
        return;
      }

      if (!force && !dirtyRef.current) {
        return;
      }

      syncingRef.current = true;
      setSyncing(true);
      try {
        await performSync();
        dirtyRef.current = false;
        const now = Date.now();
        lastSyncRef.current = now;
        setLastSync(now);
        setError(null);
      } catch (syncError) {
        const message = syncError instanceof Error ? syncError.message : 'Unable to sync right now.';
        setError(message);
        throw syncError;
      } finally {
        syncingRef.current = false;
        setSyncing(false);
      }
    },
    [enabled, performSync, throttleMs],
  );

  const requestSync = useCallback((force = false) => runSync(force), [runSync]);
  const syncNow = useCallback(() => requestSync(true), [requestSync]);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      syncingRef.current = false;
      dirtyRef.current = true;
      lastSyncRef.current = 0;
      setLastSync(0);
      setError(null);
      setSyncing(false);
      return;
    }

    requestSync(true).catch(() => undefined);
  }, [enabled, requestSync]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onVisibilityChange = () => {
      if (!document.hidden) {
        requestSync().catch(() => undefined);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, requestSync]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleOnline = () => {
      requestSync().catch(() => undefined);
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [enabled, requestSync]);

  return { requestSync, syncNow, markDirty, syncing, error, lastSync };
}
